import { Card, CardContent, CardHeader, FormControl, FormControlLabel, FormHelperText, Grid2, IconButton, Stack, Switch } from "@mui/material"
import DashboardLayout from "../../../layoutcontainers/DashboardLayout"
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSnackbar } from "../../../context/popup";
import { useLoading } from "../../../components/Loading";
import { Add, ArrowBack, Delete } from "@mui/icons-material";
import MaterialTypography from "../../../components/MaterialTypography";
import MaterialBox from "../../../components/MaterialBox";
import MaterialSelect from "../../../components/MasterSelect";
import { useInsurerRegistryFormHandling } from "./components/InsurerRegistryStateHandle";
import { useEffect, useState } from "react";
import MaterialInput from "../../../components/MaterialInput";
import MaterialButton from "../../../components/MaterialButton";
import { fetchData, fetchDataById, postData, putData } from "../../../services";
import DropDownConfig from "../../Utils/DropDownConfig";

const InsurerRegistryForm = () => {

    const [searchParams] = useSearchParams();
    const insurerRegistryId = searchParams.get("id");
    const pageType = searchParams.get("pageType");
    const pageMode = pageType === "view" ? "view" : pageType === "edit" ? "edit" : "add";
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const { setCircleLoading } = useLoading();

    const [generalInsurerOptions, setGeneralInsurerOptions] = useState([]);
    const [environmentOptions, setEnvironmentOptions] = useState([]);
    const [integrationStatusOptions, setIntegrationStatusOptions] = useState([]);
    
    const toOptions = (nodes = []) => nodes.map((n) => ({
        label: n.keyName,
        value: n.id,
        children: n.children || [],
    }));
    const [lobTree, setLobTree] = useState([]);
    const gLineOfBusinessOptions = toOptions(
        lobTree?.children || []
    );    

    const getProductOptions = (product) => product?.gLobId?.children
        ? toOptions(product.gLobId.children)
        : [];
    
    const getSubProductOptions = (product) => product?.gProductId?.children
        ? toOptions(product.gProductId.children)
        : [];

    const getVehicleCategoryOptions = (product) => product?.gSubProductId?.children
        ? toOptions(product.gSubProductId.children)
        : [];

    const { formData, setFormData, handleTopLevelChange, handleProductChange, handleEnvironmentChange, handleAddProduct, handleDeleteProduct, handleAddEnvironment, handleDeleteEnvironment, checkAllFields } = useInsurerRegistryFormHandling();

    useEffect(() => {
        if(pageMode === "view"){
            return
        }
        fetchAllDropdowns();
    }, [pageMode]);

    useEffect(() => {
        if (pageMode === "add" || !insurerRegistryId) {
            return;
        }

        //Fetch data by Pincode Id
        const fetchResponseDataById = async (insurerRegistryId) => {
            try {
                setCircleLoading(true);
                const response = await fetchDataById(`/reference/api-engine/insurer-registry/${insurerRegistryId}`);

                const responseData = response?.data;
                
                if (response?.success) {
                    const mapped = {
                        gInsurer: {
                            value: responseData?.gInsurerId,
                            label: responseData?.gInsurerName,
                        },
                        lastCheckedAt: new Date(responseData?.lastCheckedAt).toISOString().split("T")[0] || "",
                        isActive: responseData?.isActive,

                        products: (responseData?.products || []).map((product) => ({
                            gLobId: {
                            value: product.gLobId,
                            label: product.gLobName,
                            },

                            gProductId: {
                            value: product.gProductId,
                            label: product.gProductName,
                            },

                            gSubProductId: {
                            value: product.gSubProductId,
                            label: product.gSubProductName,
                            },

                            gVehicleCategoryId: product.gVehicleCategoryName
                            ? {
                                value: product.gVehicleCategoryId ?? "",
                                label: product.gVehicleCategoryName,
                                }
                            : "",

                            isActive: product.isActive,

                            environments: (product.environments || []).map((env) => ({
                            environmentId: {
                                value: env.environmentId,
                                label: env.environmentName,
                            },

                            integrationStatusId: {
                                value: env.integrationStatusId,
                                label: env.integrationStatusName,
                            },

                            onboardedAt: env?.onboardedAt ? new Date(env?.onboardedAt).toISOString().split("T")[0] : "",
                            notes: env.notes || "",
                            isActive: env.isActive,
                            })),
                        })),
                    };

                    setFormData(mapped);   //view data
                }
            } catch (error) {
                console.error("Error fetching data by ID:", error);
                showSnackbar(error?.response?.data?.message || "Network Error","error");
            }finally{
                setCircleLoading(false);
            }
        };

        fetchResponseDataById(insurerRegistryId);
    }, [pageMode]);

    //all dropdown api function
    const fetchAllDropdowns = async () => {
        setCircleLoading(true);

        try {
            const [ insurerResponse, treeResponse, environmentRes, integrationResponse ] = await Promise.allSettled([
            fetchData(`/reference/general/insurer-master?dropdown=true&isActive=true`),
            fetchData(`/reference/general/key-value-store/tree/${DropDownConfig?.generalLineOfBusiness}?maxDepth=4`),
            fetchData(`/api-engine/key-value-store?dropdown=true&code=${DropDownConfig?.apiEnvironment}`),
            fetchData(`/api-engine/key-value-store?code=${DropDownConfig?.apiIntegrationStatus}`)
            ]);

            const formattedInsurers = insurerResponse?.value?.data?.items?.map((item) => ({
                value: item?.id,
                label: item?.companyName,
            })) || [];            
            if (environmentRes.status === "fulfilled" && environmentRes.value?.success) 
                {
                setEnvironmentOptions(
                    environmentRes.value.data?.items?.map((i) => ({
                        label: i.keyName,
                        value: i.id,
                    })) || []
                );
            }
            
            if(integrationResponse?.value?.success){
                setIntegrationStatusOptions(
                    integrationResponse.value.data?.items?.map((i) => ({
                        label: i.keyName,
                        value: i.id,
                    })) || []
                );
            }
            setGeneralInsurerOptions(formattedInsurers);
            setLobTree(treeResponse?.value?.data)


        } catch (error) {
            console.error("Error fetching dropdown data:", error);
            showSnackbar("Error fetching Dropdown", "warning");
        } finally {
            setCircleLoading(false);
        }
    };

    const buildPayload = (formData) => ({
        id: insurerRegistryId,
        gInsurerId: formData.gInsurer?.value,
        isActive: formData.isActive,
        lastCheckedAt: formData.lastCheckedAt,

        products: formData.products.map((product) => ({
            gLobId: product.gLobId?.value || null,
            gProductId: product.gProductId?.value || null,
            gSubProductId: product.gSubProductId?.value || null,
            gVehicleCategoryId: product.gVehicleCategoryId?.value || null,
            isActive: product.isActive,

            environments: product.environments.map((env) => ({
            environmentId: env.environmentId?.value,
            integrationStatusId: env.integrationStatusId?.value,
            onboardedAt: env.onboardedAt ? env.onboardedAt : null,
            notes: env.notes,
            isActive: env.isActive,
            })),
        })),
    });
    

    const handleSubmitInsurerRegistry = async () => {
        //Update
        if(insurerRegistryId){
            try {
                setCircleLoading(true);
                const response = await putData(`/reference/api-engine/insurer-registry/${insurerRegistryId}`, buildPayload(formData));                
                const responseMessage = response?.message;
                if(response?.success){
                //success message
                    showSnackbar(responseMessage, "success");
                    navigate('/api-integration-engine/insurer-registry');          
                }
                else{
                    //error message
                    showSnackbar(responseMessage, "error")
                }
            } catch (error) {
                console.error('Error updating data:', error);
                showSnackbar(error?.response?.data?.message || "Network Error","error");
            }finally{
                setCircleLoading(false);
            }
        }
        else{      
        //Submit
            try {
                setCircleLoading(true);
                const response = await postData('/reference/api-engine/insurer-registry', buildPayload(formData));                
                const responseMessage = response?.message;
                if(response?.success){
                    //success message
                    showSnackbar(responseMessage, "success");
                    navigate('/api-integration-engine/insurer-registry');          
                }
                else{
                    //error message
                    showSnackbar(responseMessage, "error");
                }
            } catch (error) {
                console.error('Error posting data:', error);
                //catch error message
                showSnackbar(error?.response?.data?.message || "Network Error","error")
            }finally{
                setCircleLoading(false);
            }
        }
    };



  return (
    <DashboardLayout>
        <Card sx={{ minHeight: 500 }}>
            <CardHeader
            title={
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <IconButton onClick={() => navigate("/api-integration-engine/insurer-registry")}>
                    <ArrowBack />
                  </IconButton>
                  <Stack direction="column" spacing={0}>
                    <MaterialTypography variant="h5" fontWeight="medium">
                      Insurer Registry Details
                    </MaterialTypography>
                    <MaterialTypography variant="caption" color="text">
                      Enter New Insurer Registry Details.
                    </MaterialTypography>
                  </Stack>
                </Stack>
              </Stack>
            }
            />
            <CardContent>
                <Grid2 container spacing={1}>
                    <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                        <MaterialTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold">
                            General Insurer*
                        </MaterialTypography>
                        <MaterialSelect
                        name="gInsurer"
                        value={formData.gInsurer}
                        onChange={(value) =>
                            handleTopLevelChange({
                            target: {
                                name: "gInsurer",
                                value,
                            },
                            })
                        }
                        options={generalInsurerOptions}
                        isDisabled={pageMode === "view"}
                        />
                    </Grid2>
                    <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                        <MaterialTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold">
                            Last Checked At*
                        </MaterialTypography>
                        <MaterialInput
                        name="lastCheckedAt"
                        value={formData.lastCheckedAt}
                        onChange={handleTopLevelChange}
                        type="date"
                        success={formData.lastCheckedAtStatus === "success"}
                        error={formData.lastCheckedAtStatus === "error"}
                        disabled={pageMode === "view"}
                        />
                    </Grid2>
                    <Grid2 size={{lg:2, md:2, sm: 3, xs: 6 }}>
                        <MaterialTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold">
                            Active
                        </MaterialTypography>
                        <FormControlLabel
                        control={
                            <Switch
                                checked={formData.isActive}
                                disabled={pageMode === "view"}
                                onChange={(e) =>
                                    handleTopLevelChange({
                                        target: {
                                            name: "isActive",
                                            value: e.target.checked,
                                        },
                                    })
                                }
                                color="primary"
                            />
                        }
                        label={
                            <MaterialTypography variant="caption" fontWeight="bold">
                                {formData?.isActive ? "Yes" : "No"}
                            </MaterialTypography>
                        }
                        sx={{ marginLeft: 0 }}
                        />
                    </Grid2>
                    <Grid2 size={{lg:12, md:12, sm: 12, xs: 12 }}>
                        {(formData.products || []).map((product, index) => (
                        <MaterialBox border="1px solid #e0e0e0" borderRadius="8px" padding="16px" marginTop="8px">
                            <MaterialBox
                            display="flex"
                            justifyContent="space-between"
                            >
                                <MaterialTypography variant="h6" fontWeight="medium">
                                {index + 1}. Product Details
                                </MaterialTypography>
                                <MaterialBox
                                display="flex"
                                justifyContent="space-between">
                                    {formData.products.length > 1 && (
                                        <IconButton
                                        onClick={() => handleDeleteProduct(index)}
                                        disabled={pageMode === "view"}
                                        size="small"
                                        sx={{
                                            border: "1px solid",
                                            borderRadius: "0px",
                                            px: 1,
                                            color: "error.main",
                                            borderColor: "error.main",
                                            "&:hover": {
                                            backgroundColor: "error.light",
                                            color: "error.contrastText",
                                            },
                                        }}
                                        >
                                        <Delete fontSize="small" />
                                        </IconButton>
                                    )}
                                    <IconButton
                                        color="dark"
                                        onClick={handleAddProduct}
                                        disabled={pageMode === "view"}
                                        size="small"
                                        sx={{
                                            border: "1px solid",
                                            borderRadius: "0px",
                                            color: "success.main",
                                            borderColor: "success.main",
                                            "&:hover": {
                                                backgroundColor: "#afff9f",
                                                color: "success.contrastText",
                                        }}}
                                    >
                                        <Add /> 
                                        <MaterialTypography variant="caption" fontWeight="medium">Product</MaterialTypography>
                                    </IconButton>
                                </MaterialBox>
                            </MaterialBox>
                            <Grid2 container spacing={1}>
                                <Grid2 size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                                    <MaterialTypography component="label" variant="caption" fontWeight="bold">
                                        General Line Of Business*
                                    </MaterialTypography>

                                    <MaterialSelect
                                        value={formData.products[index].gLobId}
                                        options={gLineOfBusinessOptions}
                                        onChange={(value) => {
                                        handleProductChange(index, "gLobId", value);

                                        // reset rest of them
                                        handleProductChange(index, "gProductId", "");
                                        handleProductChange(index, "gSubProductId", "");
                                        handleProductChange(index, "gVehicleCategoryId", "");
                                        }}
                                        isDisabled={pageMode === "view"}
                                    />
                                </Grid2>

                                <Grid2 size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                                    <MaterialTypography variant="caption" fontWeight="bold">
                                        General Product*
                                    </MaterialTypography>

                                    <MaterialSelect
                                        value={formData.products[index].gProductId}
                                        options={getProductOptions(product)}
                                        onChange={(value) => {
                                        handleProductChange(index, "gProductId", value);

                                        handleProductChange(index, "gSubProductId", "");
                                        handleProductChange(index, "gVehicleCategoryId", "");
                                        }}
                                        isDisabled={pageMode === "view" || formData.products[index].gLobId === ""}
                                    />
                                </Grid2>
                 
                                <Grid2 size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                                    <MaterialTypography variant="caption" fontWeight="bold">
                                        General Sub Product 1
                                    </MaterialTypography>

                                    <MaterialSelect
                                        value={formData.products[index].gSubProductId}
                                        options={getSubProductOptions(product)}
                                        onChange={(value) => {
                                        handleProductChange(index, "gSubProductId", value);
                                        handleProductChange(index, "gVehicleCategoryId", "");
                                        }}
                                        isDisabled={pageMode === "view" || formData.products[index].gProductId === ""}
                                    />
                                </Grid2>

                                {getVehicleCategoryOptions(product).length > 0 && formData.products[index]?.gProductId?.label === "Motor Product" && (
                                <Grid2 size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                                    <MaterialTypography variant="caption" fontWeight="bold">
                                    General Sub Product 2
                                    </MaterialTypography>

                                    <MaterialSelect
                                    value={formData.products[index].gVehicleCategoryId}
                                    options={getVehicleCategoryOptions(product)}
                                    onChange={(value) =>
                                        handleProductChange(index, "gVehicleCategoryId", value)
                                    }
                                    isDisabled={pageMode === "view"}
                                    />
                                </Grid2>
                                )}                                
                                <Grid2 size={{lg:2, md:2, sm: 3, xs: 6 }}>
                                        <MaterialTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold">
                                            Active
                                        </MaterialTypography>
                                    <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.products[index].isActive}
                                            disabled={pageMode === "view"}
                                            onChange={(e) =>
                                                handleProductChange(index, "isActive", e.target.checked)
                                            }
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <MaterialTypography variant="caption" fontWeight="bold">
                                            {formData.products[index].isActive ? "Yes" : "No"}
                                        </MaterialTypography>
                                    }
                                    sx={{ marginLeft: 0 }}
                                    />
                                </Grid2>
                                <Grid2 size={{lg:12, md:12, sm: 12, xs: 12 }}>
                                    {product.environments.map((environmentData, envIndex) => (
                                    <MaterialBox border="1px solid #e0e0e0" borderRadius="8px" padding="16px" marginTop="8px">
                                        <MaterialBox
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        >
                                        <MaterialTypography variant="h6" fontWeight="medium">
                                            {envIndex + 1}. Environment Details
                                        </MaterialTypography>
                                        <MaterialBox
                                        display="flex"
                                        justifyContent="space-between">
                                            {product.environments.length > 1 && (
                                                <IconButton
                                                onClick={() => handleDeleteEnvironment(index, envIndex)}
                                                disabled={pageMode === "view"}
                                                size="small"
                                                sx={{
                                                    border: "1px solid",
                                                    borderRadius: "0px",
                                                    px: 1,
                                                    color: "error.main",
                                                    borderColor: "error.main",
                                                    "&:hover": {
                                                        backgroundColor: "error.light",
                                                        color: "error.contrastText",
                                                    },
                                                }}
                                                >
                                                    <Delete/>
                                                </IconButton>
                                            )}
                                            <IconButton
                                                onClick={() => handleAddEnvironment(index)}
                                                disabled={pageMode === "view"}
                                                size="small"
                                                color="dark"
                                                sx={{
                                                    border: "1px solid",
                                                    borderRadius: "0px",
                                                    color: "success.main",
                                                    borderColor: "success.main",
                                                    "&:hover": {
                                                        backgroundColor: "#afff9f",
                                                        color: "success.contrastText",
                                                }}}
                                            >
                                                <Add size="small"/>
                                                <MaterialTypography variant="caption" fontWeight="medium">Environment</MaterialTypography>
                                            </IconButton>
                                        </MaterialBox>
                                        </MaterialBox>
                                        <Grid2 container spacing={1}>
                                            <Grid2 size={{lg:6, md:6, sm: 6, xs: 12 }}>
                                                <MaterialTypography
                                                component="label"
                                                variant="caption"
                                                fontWeight="bold">
                                                    Environment*
                                                </MaterialTypography>
                                                <MaterialSelect
                                                name="environmentId"
                                                value={environmentData.environmentId}
                                                options={environmentOptions}
                                                onChange={(value) => handleEnvironmentChange(index, envIndex, "environmentId", value)}
                                                isDisabled={pageMode === "view"}
                                                />
                                            </Grid2>
                                            <Grid2 size={{lg:6, md:6, sm: 6, xs: 12 }}>
                                                <MaterialTypography
                                                component="label"
                                                variant="caption"
                                                fontWeight="bold">
                                                    Integration Status*
                                                </MaterialTypography>
                                                <MaterialSelect
                                                name="integrationStatusId"
                                                options={integrationStatusOptions}
                                                value={environmentData.integrationStatusId}
                                                onChange={(value) => handleEnvironmentChange(index, envIndex, "integrationStatusId", value)}
                                                isDisabled={pageMode === "view"}
                                                />
                                            </Grid2>
                                            <Grid2 size={{lg:6, md:6, sm: 6, xs: 12 }}>
                                                <MaterialTypography
                                                component="label"
                                                variant="caption"
                                                fontWeight="bold">
                                                    Onboarded At
                                                </MaterialTypography>
                                                <MaterialInput
                                                name="onboardedAt"
                                                type="date"
                                                value={environmentData.onboardedAt}
                                                onChange={(e) =>  handleEnvironmentChange(index, envIndex, "onboardedAt", e.target.value)}
                                                disabled={pageMode === "view"}
                                                />
                                            </Grid2>
                                            <Grid2 size={{lg:6, md:6, sm: 6, xs: 12 }}>
                                                <MaterialTypography
                                                component="label"
                                                variant="caption"
                                                fontWeight="bold">
                                                    Notes
                                                </MaterialTypography>
                                                <MaterialInput
                                                name="notes"
                                                placeholder="eg. Registered with Accident policy"
                                                value={environmentData.notes}
                                                onChange={(e) => handleEnvironmentChange(index, envIndex, "notes", e.target.value)}
                                                disabled={pageMode === "view"}
                                                />
                                            </Grid2>
                                            <Grid2 size={{lg:2, md:2, sm: 4, xs: 6 }}>
                                            <MaterialTypography
                                            component="label"
                                            variant="caption"
                                            fontWeight="bold">
                                                Active
                                            </MaterialTypography>
                                            <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={environmentData.isActive}
                                                    disabled={pageMode === "view"}
                                                    onChange={(e) =>
                                                        handleEnvironmentChange(index, envIndex, "isActive", e.target.checked)
                                                    }
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <MaterialTypography variant="caption" fontWeight="bold">
                                                    {environmentData?.isActive ? "Yes" : "No"}
                                                </MaterialTypography>
                                            }
                                            sx={{ marginLeft: 0 }}
                                            />
                                            </Grid2>
                                        </Grid2>
                                    </MaterialBox>
                                    ))}
                                </Grid2>
                            </Grid2>
                        </MaterialBox>
                        ))}
                    </Grid2>
                </Grid2>
            </CardContent>
                <MaterialBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                >
                    {pageMode !== "view" && (
                    <>
                        <MaterialButton
                        sx={{ m: 2 }}
                        variant="outlined"
                        color="dark"
                        onClick={() => navigate("/api-integration-engine/insurer-registry")}
                        >
                        Cancel
                        </MaterialButton>
                        <MaterialButton
                        sx={{ m: 2 }}
                        variant="gradient"
                        color="dark"
                        disabled={!checkAllFields()}
                        onClick={handleSubmitInsurerRegistry}
                        >
                        {pageMode === "edit" ? "Update" : "Submit"}
                        </MaterialButton>
                    </>
                    )}
                </MaterialBox>
        </Card> 
    </DashboardLayout>
  )
}

export default InsurerRegistryForm